import React from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { pagePrimaryButtonSx } from "../../styles/pageModernStyles";
import { ORG_BRANDS, normalizeOrgBrand } from "./skillAgentOrgBrand";

const pop = keyframes`
  0%, 12% { opacity: 0; transform: translateY(8px) scale(0.96); }
  28%, 78% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(4px) scale(0.98); }
`;

const pulse = keyframes`
  0%, 20% { transform: scale(0.85); opacity: 0.5; }
  45%, 70% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.9); opacity: 0.55; }
`;

const slide = keyframes`
  0%, 10% { transform: translateX(-10px); opacity: 0; }
  30%, 75% { transform: translateX(0); opacity: 1; }
  100% { transform: translateX(6px); opacity: 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const HUB_OPTIONS = [
  {
    id: "meeting",
    accent: "#0f766e",
    tint: "#e6f4f1",
    projectOnly: true,
  },
  {
    id: "task",
    accent: "#4338ca",
    tint: "#eef0fb",
  },
  {
    id: "aiDesign",
    accent: "#0f766e",
    tint: "#e6f7f3",
  },
  {
    id: "search",
    accent: "#0f766e",
    tint: "#e8f5f2",
  },
  {
    id: "moodboard",
    accent: "#9a3412",
    tint: "#f8eee9",
  },
  {
    id: "resumen",
    accent: "#0369a1",
    tint: "#e8f3fa",
  },
  {
    id: "rapport",
    accent: "#a16207",
    tint: "#f7f1e4",
  },
  {
    id: "clientInfo",
    accent: "#0277bd",
    tint: "#e3f2fd",
  },
  {
    id: "produccion",
    accent: "#bf360c",
    tint: "#fbe9e7",
  },
];

function Stage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 72,
        overflow: "hidden",
        bgcolor: tint,
      }}
    >
      {children}
    </Box>
  );
}

function MeetingPreview({ accent }) {
  return (
    <Stage tint="#e6f4f1">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 18 + i * 28,
            top: 22,
            width: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: i === 0 ? accent : alpha(accent, 0.35 + i * 0.15),
            animation: `${pulse} 3.6s ease-in-out infinite`,
            animationDelay: `${i * 0.2}s`,
            ...motionSafe,
          }}
        />
      ))}
      <Box
        sx={{
          position: "absolute",
          left: 22,
          bottom: 14,
          width: 70,
          height: 8,
          borderRadius: 1,
          bgcolor: alpha(accent, 0.25),
          animation: `${slide} 3.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </Stage>
  );
}

function TaskPreview({ accent }) {
  return (
    <Stage tint="#eef0fb">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 16 + i * 26,
            top: 14 + (i % 2) * 4,
            width: 22,
            height: 44,
            borderRadius: 1,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: alpha(accent, 0.25),
            boxShadow: `inset 0 -10px 0 ${alpha(accent, 0.12)}`,
            animation: `${pop} 4.2s ease-in-out infinite`,
            animationDelay: `${i * 0.22}s`,
            ...motionSafe,
          }}
        />
      ))}
    </Stage>
  );
}

function AiDesignPreview({ accent }) {
  return (
    <Stage tint="#e6f7f3">
      <Box
        sx={{
          position: "absolute",
          left: "28%",
          top: 16,
          width: 36,
          height: 44,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.35),
          animation: `${float} 3.4s ease-in-out infinite`,
          ...motionSafe,
          "&::before": {
            content: '""',
            position: "absolute",
            left: "50%",
            top: 6,
            width: 14,
            height: 8,
            transform: "translateX(-50%)",
            borderRadius: "0 0 8px 8px",
            border: `1.5px solid ${alpha(accent, 0.45)}`,
            borderTop: "none",
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: "22%",
          top: 20,
          width: 40,
          height: 36,
          borderRadius: 1.5,
          background: `linear-gradient(145deg, ${alpha(accent, 0.55)}, ${alpha("#7c3aed", 0.55)})`,
          boxShadow: `0 6px 14px ${alpha(accent, 0.2)}`,
          animation: `${float} 3.8s ease-in-out infinite`,
          animationDelay: "0.2s",
          ...motionSafe,
        }}
      />
    </Stage>
  );
}

function SearchPreview({ accent }) {
  return (
    <Stage tint="#e8f5f2">
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "42%",
          width: 28,
          height: 28,
          ml: "-14px",
          mt: "-14px",
          borderRadius: "50%",
          border: `3px solid ${accent}`,
          animation: `${pulse} 3.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: "58%",
          top: "58%",
          width: 14,
          height: 3,
          borderRadius: 1,
          bgcolor: accent,
          transform: "rotate(40deg)",
          animation: `${float} 3.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </Stage>
  );
}

function MoodboardPreview({ accent }) {
  return (
    <Stage tint="#f8eee9">
      {[0, 1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 14 + (i % 2) * 36,
            top: 12 + Math.floor(i / 2) * 28,
            width: 30,
            height: 22,
            borderRadius: 0.75,
            bgcolor: i % 2 ? accent : alpha(accent, 0.45),
            animation: `${pop} 4s ease-in-out infinite`,
            animationDelay: `${i * 0.15}s`,
            ...motionSafe,
          }}
        />
      ))}
    </Stage>
  );
}

function ResumenPreview({ accent }) {
  return (
    <Stage tint="#e8f3fa">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 18,
            top: 16 + i * 16,
            width: 58 + (2 - i) * 12,
            height: 8,
            borderRadius: 1,
            bgcolor: alpha(accent, 0.25 + i * 0.15),
            animation: `${slide} 3.8s ease-in-out infinite`,
            animationDelay: `${i * 0.18}s`,
            ...motionSafe,
          }}
        />
      ))}
    </Stage>
  );
}

function RapportPreview({ accent }) {
  return (
    <Stage tint="#f7f1e4">
      <Box
        sx={{
          position: "absolute",
          left: 22,
          top: 14,
          width: 48,
          height: 44,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2px",
          animation: `${pop} 4.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              bgcolor: i % 2 ? accent : alpha(accent, 0.4),
            }}
          />
        ))}
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 18,
          top: 20,
          width: 8,
          height: 36,
          borderRadius: 1,
          bgcolor: alpha(accent, 0.55),
          animation: `${float} 3.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </Stage>
  );
}

function ClientInfoPreview({ accent }) {
  return (
    <Stage tint="#e3f2fd">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 16 + i * 22,
            top: 22,
            width: 18,
            height: 28,
            borderRadius: 0.75,
            bgcolor: alpha(accent, 0.35 + i * 0.2),
            animation: `${pop} 4s ease-in-out infinite`,
            animationDelay: `${i * 0.12}s`,
            ...motionSafe,
          }}
        />
      ))}
    </Stage>
  );
}

function ProduccionPreview({ accent }) {
  return (
    <Stage tint="#fbe9e7">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 20 + i * 24,
            top: 18 + (i % 2) * 8,
            width: 20,
            height: 20,
            borderRadius: 1,
            border: `2px solid ${accent}`,
            bgcolor: alpha(accent, 0.2),
            animation: `${float} 3.6s ease-in-out infinite`,
            animationDelay: `${i * 0.14}s`,
            ...motionSafe,
          }}
        />
      ))}
    </Stage>
  );
}

const PREVIEWS = {
  meeting: MeetingPreview,
  task: TaskPreview,
  aiDesign: AiDesignPreview,
  search: SearchPreview,
  moodboard: MoodboardPreview,
  resumen: ResumenPreview,
  rapport: RapportPreview,
  clientInfo: ClientInfoPreview,
  produccion: ProduccionPreview,
};

function OptionCard({ id, accent, tint, selected, title, body, onSelect }) {
  const theme = useTheme();
  const Preview = PREVIEWS[id] || TaskPreview;
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      sx={{
        p: 0,
        textAlign: "left",
        cursor: "pointer",
        borderRadius: 3,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? accent : "divider",
        bgcolor: "background.paper",
        boxShadow: selected ? `0 10px 28px ${alpha(accent, 0.18)}` : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        font: "inherit",
        color: "inherit",
        "&:hover": {
          borderColor: selected ? accent : alpha(accent, 0.45),
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Preview accent={accent} tint={tint} />
        {selected ? (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: accent,
              bgcolor: "#fff",
              borderRadius: "50%",
              fontSize: 22,
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ px: 1.25, py: 1.1 }}>
        <Typography fontWeight={800} fontSize="0.9rem">
          {title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Diálogo Compás: elegir qué crear bajo un proyecto / reunión.
 * Misma familia visual que DesignPick / RapportPick (cards + motion).
 */
export default function SkillAgentHubCreateDialog({
  open,
  onClose,
  contextNode = null,
  allowMeeting = false,
  onPick,
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState("");
  // Si el nodo ya es orgBranch / carpeta bajo NML|STAGE, no preguntar.
  const hasInherited = ["NML", "STAGE"].includes(
    String(contextNode?.OrgBrand || contextNode?.orgBrand || "")
      .trim()
      .toUpperCase(),
  );
  const [orgBrand, setOrgBrand] = React.useState(
    hasInherited
      ? normalizeOrgBrand(contextNode?.OrgBrand || contextNode?.orgBrand)
      : "",
  );

  React.useEffect(() => {
    if (!open) {
      setSelected("");
      setOrgBrand(
        hasInherited
          ? normalizeOrgBrand(contextNode?.OrgBrand || contextNode?.orgBrand)
          : "",
      );
    }
  }, [open, hasInherited, contextNode?.OrgBrand, contextNode?.orgBrand]);

  const branch =
    [
      contextNode?.clientLabel || contextNode?.CustomerName,
      contextNode?.label,
      contextNode?.seasonLabel || contextNode?.Temporada,
    ]
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .join(" · ") || t("skillAgent.common.emDash");

  const options = HUB_OPTIONS.filter((o) => !o.projectOnly || allowMeeting);

  const handleConfirm = () => {
    if (!selected) return;
    const brand = hasInherited
      ? normalizeOrgBrand(contextNode?.OrgBrand || contextNode?.orgBrand)
      : normalizeOrgBrand(orgBrand);
    if (!hasInherited && !orgBrand) return;
    onPick?.(selected, contextNode, { OrgBrand: brand });
  };

  const canContinue = Boolean(selected) && (hasInherited || Boolean(orgBrand));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionProps={{ timeout: 280 }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={800}>
          {t("skillAgent.dialog.hubCreateTitle", {
            defaultValue: "¿Qué quieres crear?",
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("skillAgent.dialog.hubCreateLead", {
            defaultValue:
              "Elige la pieza. Luego rellenas el detalle en el siguiente paso.",
          })}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1.25 }}>
          <Box
            sx={{
              px: 1.5,
              py: 1.1,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.primary.main, 0.06),
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              {t("skillAgent.dialog.treeBranchLabel")}
            </Typography>
            <Typography variant="body1" fontWeight={800} noWrap>
              {branch}
            </Typography>
          </Box>
          {!hasInherited ? (
            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                {t("skillAgent.dialog.orgBrandTitle", {
                  defaultValue: "¿Para qué empresa?",
                })}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                }}
              >
                {ORG_BRANDS.map((brand) => {
                  const selectedBrand = orgBrand === brand;
                  const accent = brand === "NML" ? "#6a1b9a" : "#00897b";
                  return (
                    <Box
                      key={brand}
                      component="button"
                      type="button"
                      onClick={() => setOrgBrand(brand)}
                      aria-pressed={selectedBrand}
                      sx={{
                        py: 1.35,
                        px: 1.5,
                        borderRadius: 2.25,
                        border: "2px solid",
                        borderColor: selectedBrand ? accent : "divider",
                        bgcolor: selectedBrand
                          ? alpha(accent, 0.1)
                          : "background.paper",
                        cursor: "pointer",
                        font: "inherit",
                        fontWeight: 800,
                        color: selectedBrand ? accent : "text.primary",
                        textAlign: "center",
                        "&:hover": {
                          borderColor: accent,
                          bgcolor: alpha(accent, 0.08),
                        },
                      }}
                    >
                      {brand}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("skillAgent.dialog.orgBrandInherited", {
                defaultValue: "Se creará en {{brand}}.",
                brand: normalizeOrgBrand(
                  contextNode?.OrgBrand || contextNode?.orgBrand,
                ),
              })}
            </Typography>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              },
              gap: 1.25,
            }}
          >
            {options.map((opt) => (
              <OptionCard
                key={opt.id}
                id={opt.id}
                accent={opt.accent}
                tint={opt.tint}
                selected={selected === opt.id}
                title={t(`skillAgent.dialog.hubCreate.${opt.id}.title`, {
                  defaultValue: opt.id,
                })}
                body={t(`skillAgent.dialog.hubCreate.${opt.id}.body`, {
                  defaultValue: "",
                })}
                onSelect={() => setSelected(opt.id)}
              />
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t("skillAgent.common.cancel")}</Button>
        <Button
          variant="contained"
          sx={pagePrimaryButtonSx}
          disabled={!canContinue}
          onClick={handleConfirm}
        >
          {t("skillAgent.dialog.hubCreateContinue", {
            defaultValue: "Continuar",
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
